/**
 * Find an Accountant sub-page plus its pinned header.
 *
 * H1 removed because the sticky breadcrumb already ends with
 * "Find an accountant". View profile button navigates to the
 * dedicated profile page; Contact stays as a Phase A placeholder.
 */

import { useCallback, useId, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { useFindAccountants } from "../../../../hooks/useFindAccountants";
import type { FindSortField } from "./findAccountantTypes";
import { FilterBar } from "./components/FilterBar";
import { AccountantResultCard } from "./components/AccountantResultCard";

export function FindAccountantHeader() {
  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    navigate("/accounting/my-accountant");
  }, [navigate]);

  return (
    <div style={headerBarStyle}>
      <button
        type="button"
        style={backBtnStyle}
        onClick={handleBack}
        aria-label="Back to My accountant"
      >
        <ArrowLeft size={13} strokeWidth={2.2} />
        Back
      </button>
      <nav style={crumbsStyle} aria-label="Breadcrumb">
        <Link to="/accounting" style={crumbLinkStyle}>
          Accounting
        </Link>
        <span style={crumbSepStyle}>/</span>
        <Link to="/accounting/my-accountant" style={crumbLinkStyle}>
          My accountant
        </Link>
        <span style={crumbSepStyle}>/</span>
        <span style={crumbCurrentStyle}>Find an accountant</span>
      </nav>
    </div>
  );
}

const SORT_OPTIONS: { value: FindSortField; label: string }[] = [
  { value: "distance", label: "Closest first" },
  { value: "experience", label: "Most experienced" },
  { value: "name", label: "Name (A to Z)" },
];

export default function FindAccountantPage() {
  const uid = useId().replace(/:/g, "");
  const styleTag = useFindPageStyles(uid);
  const navigate = useNavigate();
  const {
    data,
    filters,
    setFilters,
    resetFilters,
    sortField,
    setSortField,
  } = useFindAccountants();

  const resultCount = data.length;

  const hasActiveFilters = useMemo(() => {
    return (
      filters.location.trim() !== "" ||
      filters.services.length > 0 ||
      filters.industry !== "" ||
      filters.products.length > 0 ||
      filters.distanceKm !== 50
    );
  }, [filters]);

  const handleViewProfile = useCallback(
    (id: string) => {
      navigate(`/accounting/my-accountant/find/${id}`);
    },
    [navigate],
  );

  const handleContact = useCallback((id: string) => {
    window.alert(`Phase A: contact flow for ${id} ships in Phase B.`);
  }, []);

  return (
    <>
      {styleTag}
      <div className={`fap-${uid}-page`}>
        <p className={`fap-${uid}-lead`}>
          Browse Novala-certified accountants near you. Every professional
          below is a verified CPA with hands-on experience in Novala Payroll
          and small business accounting.
        </p>

        <FilterBar
          filters={filters}
          onFiltersChange={setFilters}
          onSearch={() => {
            /* no-op */
          }}
        />

        <div className={`fap-${uid}-results-head`}>
          <p className={`fap-${uid}-count`}>
            <strong>{resultCount}</strong>{" "}
            {resultCount === 1 ? "accountant" : "accountants"} found
            {hasActiveFilters && (
              <>
                {" "}
                <button
                  type="button"
                  className={`fap-${uid}-reset`}
                  onClick={resetFilters}
                >
                  Clear filters
                </button>
              </>
            )}
          </p>
          <label className={`fap-${uid}-sort`}>
            <span className={`fap-${uid}-sort-label`}>Sort by</span>
            <select
              className={`fap-${uid}-sort-select`}
              value={sortField}
              onChange={(e) => setSortField(e.target.value as FindSortField)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {resultCount === 0 ? (
          <EmptyResults uid={uid} onReset={resetFilters} />
        ) : (
          <div className={`fap-${uid}-list`}>
            {data.map((a) => (
              <AccountantResultCard
                key={a.id}
                accountant={a}
                onViewProfile={handleViewProfile}
                onContact={handleContact}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function EmptyResults({
  uid,
  onReset,
}: {
  uid: string;
  onReset: () => void;
}) {
  return (
    <div className={`fap-${uid}-empty`}>
      <div className={`fap-${uid}-empty-icon`} aria-hidden="true">
        <Search size={28} strokeWidth={1.6} />
      </div>
      <h2 className={`fap-${uid}-empty-title`}>No accountants match your filters</h2>
      <p className={`fap-${uid}-empty-body`}>
        Try widening the distance, removing a service, or clearing a filter.
      </p>
      <button
        type="button"
        className={`fap-${uid}-empty-cta`}
        onClick={onReset}
      >
        Clear all filters
      </button>
    </div>
  );
}

const headerBarStyle: React.CSSProperties = {
  padding: "14px 32px",
  borderBottom: "1px solid #E7EAF0",
  background: "#ffffff",
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexShrink: 0,
};

const backBtnStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #D8DEE6",
  color: "#0E1A1A",
  padding: "6px 11px 6px 9px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const crumbsStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontSize: 12.5,
};

const crumbLinkStyle: React.CSSProperties = {
  color: "#6B7684",
  textDecoration: "none",
  fontWeight: 500,
};

const crumbSepStyle: React.CSSProperties = {
  color: "#B0B9C3",
  fontWeight: 400,
};

const crumbCurrentStyle: React.CSSProperties = {
  color: "#0E1A1A",
  fontWeight: 600,
};

function useFindPageStyles(uid: string) {
  const css = `
    .fap-${uid}-page { width: 100%; max-width: 1120px; margin: 0 auto; }
    .fap-${uid}-lead { font-size: 14px; color: #3A4A52; line-height: 1.55; margin: 4px 0 24px; max-width: 680px; }
    .fap-${uid}-results-head { display: flex; align-items: center; justify-content: space-between; padding: 4px 2px 14px; gap: 14px; flex-wrap: wrap; }
    .fap-${uid}-count { margin: 0; font-size: 13.5px; color: #3A4A52; }
    .fap-${uid}-count strong { color: #0E1A1A; font-weight: 700; }
    .fap-${uid}-reset { background: transparent; border: 0; padding: 0; margin-left: 10px; font-family: inherit; font-size: 12.5px; color: #0F6E56; font-weight: 600; cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
    .fap-${uid}-reset:hover { color: #0A5544; }
    .fap-${uid}-sort { display: inline-flex; align-items: center; gap: 8px; }
    .fap-${uid}-sort-label { font-size: 12.5px; color: #6B7684; font-weight: 500; }
    .fap-${uid}-sort-select { font-family: inherit; font-size: 13px; color: #0E1A1A; padding: 7px 28px 7px 10px; border: 1px solid #D8DEE6; border-radius: 7px; background: #ffffff url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%233A4A52' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>") no-repeat right 10px center; -webkit-appearance: none; appearance: none; cursor: pointer; font-weight: 500; transition: border-color 160ms; }
    .fap-${uid}-sort-select:hover { border-color: #0E1A1A; }
    .fap-${uid}-sort-select:focus { outline: 0; border-color: #0E1A1A; box-shadow: 0 0 0 3px rgba(14, 26, 26, 0.08); }
    .fap-${uid}-list { display: flex; flex-direction: column; }
    .fap-${uid}-empty { background: #ffffff; border: 1px solid #E7EAF0; border-radius: 10px; padding: 48px 24px 44px; text-align: center; max-width: 540px; margin: 10px auto 0; }
    .fap-${uid}-empty-icon { width: 56px; height: 56px; border-radius: 50%; background: #F3F5F5; color: #3A4A52; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 16px; }
    .fap-${uid}-empty-title { font-size: 16px; font-weight: 600; color: #0E1A1A; margin: 0 0 8px; }
    .fap-${uid}-empty-body { font-size: 13.5px; color: #3A4A52; line-height: 1.55; margin: 0 0 20px; }
    .fap-${uid}-empty-cta { background: #0E1A1A; color: #ffffff; border: 0; padding: 9px 18px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: transform 160ms, box-shadow 160ms; }
    .fap-${uid}-empty-cta:hover { transform: translateY(-1px); box-shadow: 0 4px 10px -2px rgba(14, 26, 26, 0.22); }
  `;
  return <style>{css}</style>;
}